import {
  Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LogBugService, LogBugPayload } from '../../services/log-bug.service';
import { ToastService } from '../../services/toast.service';
import { LogBugUiService } from '../../services/log-bug-ui.service';
import { SelectDropdownComponent } from '../../../shared/components/select-dropdown/select-dropdown.component';

/** Modules offered in the dropdown — keep in sync with the backend allow-list. */
const MODULES = [
  'Home Page',
  'AI Planning Assistant',
  'Targeting Sheet',
  'Brand Safety',
  'Avails Meter',
  'Forecast',
  'Other',
] as const;

const SEVERITIES  = ['High', 'Medium', 'Low'] as const;
const FREQUENCIES = ['Always', 'Sometimes', 'Exact Number'] as const;

/** Client-side mirror of the backend upload rules (fail fast, better UX). */
const MAX_FILE_BYTES = 50 * 1024 * 1024; // 50 MB
const MAX_FILES      = 5;
const ACCEPTED_MIME  = /^(image\/(png|jpe?g|gif|webp|bmp)|video\/(mp4|webm|quicktime|x-msvideo))$/i;

/**
 * "Log Bug" modal — opened from the user menu via LogBugUiService. App-wide:
 * mounted once in the shell so it's reachable from every screen. Designed to be
 * extended — new fields slot into the reactive form + payload.
 */
@Component({
  selector: 'sp-log-bug-widget',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SelectDropdownComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './log-bug-widget.component.html',
  styleUrls: ['./log-bug-widget.component.scss'],
})
export class LogBugWidgetComponent implements OnInit, OnDestroy {
  readonly modules     = MODULES;
  readonly severities  = SEVERITIES;
  readonly frequencies = FREQUENCIES;

  open       = false;
  submitting = false;
  uploadPercent = 0;
  hasAttachments = false; // drives whether the progress bar is meaningful
  dragOver   = false;
  error      = '';

  attachments: File[] = [];

  form = this.fb.nonNullable.group({
    module:         ['', Validators.required],
    description:    ['', [Validators.required, Validators.minLength(5)]],
    severity:       ['Medium' as (typeof SEVERITIES)[number], Validators.required],
    frequencyType:  ['Always' as (typeof FREQUENCIES)[number], Validators.required],
    frequencyCount: [null as number | null],
    planId:         [''],
  });

  private submitSub?: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    private fb:        FormBuilder,
    private logBug:    LogBugService,
    private toast:     ToastService,
    private logBugUi:  LogBugUiService,
    private router:    Router,
    private cdr:       ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Opened from the user menu (or anywhere) via the shared UI service.
    this.logBugUi.open$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.openModal());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.submitSub?.unsubscribe();
  }

  get isExactNumber(): boolean {
    return this.form.controls.frequencyType.value === 'Exact Number';
  }

  // ── Open / close ────────────────────────────────────────────────────────
  openModal(): void {
    this.resetForm();
    this.form.patchValue({ planId: this.detectActivePlanId() });
    this.open = true;
    this.cdr.markForCheck();
  }

  closeModal(): void {
    if (this.submitting) return; // don't abandon an in-flight upload
    this.open = false;
    this.cdr.markForCheck();
  }

  // ── Attachments: file input + drag & drop ────────────────────────────────
  onFilesPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(input.files);
    input.value = ''; // allow re-selecting the same file
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
    this.cdr.markForCheck();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    this.cdr.markForCheck();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    this.addFiles(event.dataTransfer?.files ?? null);
  }

  removeAttachment(index: number): void {
    this.attachments.splice(index, 1);
    this.hasAttachments = this.attachments.length > 0;
    this.cdr.markForCheck();
  }

  private addFiles(list: FileList | null): void {
    if (!list) return;
    this.error = '';

    for (const file of Array.from(list)) {
      if (this.attachments.length >= MAX_FILES) {
        this.error = `You can attach up to ${MAX_FILES} files.`;
        break;
      }
      if (!ACCEPTED_MIME.test(file.type)) {
        this.error = 'Only image and video files are supported.';
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        this.error = `"${file.name}" exceeds the 50 MB limit.`;
        continue;
      }
      this.attachments.push(file);
    }

    this.hasAttachments = this.attachments.length > 0;
    this.cdr.markForCheck();
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // ── Submit ────────────────────────────────────────────────────────────────
  submit(): void {
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const raw = this.form.getRawValue();
    const payload: LogBugPayload = {
      module:      raw.module,
      description: raw.description.trim(),
      severity:    raw.severity,
      frequency: {
        type:  raw.frequencyType,
        count: raw.frequencyType === 'Exact Number' ? (raw.frequencyCount ?? null) : null,
      },
      planId:      raw.planId.trim(),
      attachments: this.attachments,
    };

    this.submitting    = true;
    this.uploadPercent = 0;
    this.error         = '';
    this.cdr.markForCheck();

    this.submitSub = this.logBug.submit(payload).subscribe({
      next: ev => {
        if (ev.kind === 'progress') {
          this.uploadPercent = ev.percent;
          this.cdr.markForCheck();
        } else {
          this.submitting = false;
          this.open = false;
          this.resetForm();
          this.toast.showSuccess(
            'Thank you!',
            'Your bug report has been submitted successfully.',
          );
          this.cdr.markForCheck();
        }
      },
      error: () => {
        this.submitting = false;
        this.error = 'Something went wrong while submitting. Please try again.';
        this.cdr.markForCheck();
      },
    });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  private resetForm(): void {
    this.submitSub?.unsubscribe();
    this.form.reset({
      module: '',
      description: '',
      severity: 'Medium',
      frequencyType: 'Always',
      frequencyCount: null,
      planId: '',
    });
    this.attachments    = [];
    this.hasAttachments = false;
    this.uploadPercent  = 0;
    this.submitting     = false;
    this.error          = '';
  }

  /** Pull the plan id out of the current URL (/plan/:id), ignoring '/plan/new'. */
  private detectActivePlanId(): string {
    const match = /\/plan\/([^/?#]+)/.exec(this.router.url);
    const id = match?.[1] ?? '';
    return id && id !== 'new' ? decodeURIComponent(id) : '';
  }
}
